import {useMemo, useState} from 'react';

import {parseSortState} from '../components/ListSortingSelect/list-sort.types';

import type {
  ListOnSortingChange,
  ListSortConfig,
  ListSortingState
} from '../components/ListSortingSelect';

interface UseListSortingReturn<T> {
  sorting: ListSortingState;
  onSortingChange: ListOnSortingChange;
  sortedItems: T[];
}

/** Pass both to control the sort from outside, e.g. a sort remembered in storage. */
interface UseListSortingOptions {
  sorting?: ListSortingState;
  onSortingChange?: ListOnSortingChange;
}

export function useListSorting<T>(
  items: readonly T[],
  config: ListSortConfig<T>,
  options: UseListSortingOptions = {}
): UseListSortingReturn<T> {
  const [ownSorting, setOwnSorting] = useState<ListSortingState>([]);
  const sorting = options.sorting ?? ownSorting;
  const onSortingChange = options.onSortingChange ?? setOwnSorting;

  const sortedItems = useMemo(() => {
    const {field, desc} = parseSortState(sorting, config);
    if (!field) return [...items];

    const fieldConfig = config.fields[field];
    if (!fieldConfig) return [...items];

    const collator = new Intl.Collator(undefined, {numeric: true, sensitivity: 'base'});
    return [...items].sort((a, b) => {
      const aVal = fieldConfig.getValue(a);
      const bVal = fieldConfig.getValue(b);
      if (aVal === null && bVal === null) return 0;
      if (aVal === null) return 1;
      if (bVal === null) return -1;
      const comparison =
        typeof aVal === 'number' && typeof bVal === 'number'
          ? aVal - bVal
          : collator.compare(String(aVal), String(bVal));
      return desc ? -comparison : comparison;
    });
  }, [items, sorting, config]);

  return {sorting, onSortingChange, sortedItems};
}
